/**
 * Admin Lists page — segmented client lists for re-engagement.
 *
 *   /admin/$slug/lists
 *
 * Gated to Large business via PlanGate (the server enforces the same
 * gate at `/api/admin/:slug/lists/*`). Surfaces two lists in MVP:
 *   - Follow-ups        — clients due for a re-booking based on the
 *                         service's recommended frequency
 *   - Lost clients      — clients whose most recent booking was
 *                         cancelled and who have no future booking
 *
 * Each list lets you choose a time window, preview the top rows, and
 * download a CSV. The CSV button surfaces a confirmation acknowledging
 * that contacting clients without `marketing_consent = yes` would
 * breach the Spam Act 2003.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bookaburra/ui';
import {
  AlertTriangle,
  Download,
  RotateCcw,
  UserX,
} from 'lucide-react';
import {
  fetchFollowUpsPreview,
  fetchLostClientsPreview,
  followUpsCsvUrl,
  lostClientsCsvUrl,
  type FollowUpRow,
  type LostClientRow,
} from '../booking-flow/lists-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
import { PlanGate } from '../components/PlanGate';

export const Route = createFileRoute('/admin/$slug/lists')({
  component: ListsPage,
});

type FollowUpWindow = 30 | 60 | 90 | 'overdue';
type LostWindow = 30 | 60 | 90 | 180;

function ListsPage() {
  const { slug } = Route.useParams();
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Lists · ${tenantQ.data.tenant.name} Admin` : 'Lists',
  );

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Lists</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Pre-built client lists for re-engagement. Preview here, then
          export a CSV to drop into your marketing platform.
        </p>
      </header>

      <PlanGate slug={slug} requiredPlan="large-business">
        <div className="space-y-10">
          <FollowUpsCard slug={slug} />
          <LostClientsCard slug={slug} />
        </div>
      </PlanGate>
    </main>
  );
}

/* ------------------------------ Follow-ups ------------------------------ */

const FOLLOW_UP_OPTIONS: { value: FollowUpWindow; label: string }[] = [
  { value: 'overdue', label: 'Already overdue' },
  { value: 30, label: 'Due in 30 days' },
  { value: 60, label: 'Due in 60 days' },
  { value: 90, label: 'Due in 90 days' },
];

function FollowUpsCard({ slug }: { slug: string }) {
  const [window, setWindow] = useState<FollowUpWindow>(30);
  const [confirming, setConfirming] = useState(false);

  const followUpsQ = useQuery({
    queryKey: ['admin-lists', slug, 'follow-ups', window],
    queryFn: () => fetchFollowUpsPreview(slug, window),
  });

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-2 text-primary">
            <RotateCcw className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Follow-ups</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Clients whose last service is due for a re-booking, based on
              each service&rsquo;s recommended frequency.
            </p>
          </div>
        </div>
        <Button
          leadingIcon={<Download className="h-4 w-4" />}
          onClick={() => setConfirming(true)}
          disabled={!followUpsQ.data || followUpsQ.data.totalCount === 0}
        >
          Export CSV
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {FOLLOW_UP_OPTIONS.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => setWindow(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              window === opt.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background text-muted-foreground hover:border-primary/40'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {followUpsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : followUpsQ.error ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&rsquo;t load list</AlertTitle>
            <AlertDescription>
              {(followUpsQ.error as Error).message}
            </AlertDescription>
          </Alert>
        ) : (
          <FollowUpsPreview
            totalCount={followUpsQ.data!.totalCount}
            rows={followUpsQ.data!.preview}
          />
        )}
      </div>

      <ExportConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        rowCount={followUpsQ.data?.totalCount ?? 0}
        downloadUrl={followUpsCsvUrl(slug, window)}
        listLabel="Follow-ups"
      />
    </Card>
  );
}

function FollowUpsPreview({
  totalCount,
  rows,
}: {
  totalCount: number;
  rows: FollowUpRow[];
}) {
  if (totalCount === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        No clients in this window. Either nobody&rsquo;s due, or you
        haven&rsquo;t set a rebook frequency on any service yet.
      </p>
    );
  }
  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {totalCount} {totalCount === 1 ? 'client' : 'clients'} ·
        showing first {Math.min(rows.length, totalCount)}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Client</th>
              <th className="px-4 py-2 text-left">Last service</th>
              <th className="px-4 py-2 text-left">Due</th>
              <th className="px-4 py-2 text-left">Consent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.clientId} className="border-t border-border">
                <td className="px-4 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.email ?? r.phone ?? '—'}
                  </div>
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {r.serviceName}
                </td>
                <td className="px-4 py-2">
                  {r.dueInDays <= 0 ? (
                    <Badge variant="warning">
                      {Math.abs(r.dueInDays)}d overdue
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">
                      in {r.dueInDays}d
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {r.marketingConsent ? (
                    <Badge variant="success">Yes</Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Lost clients ----------------------------- */

const LOST_OPTIONS: { value: LostWindow; label: string }[] = [
  { value: 30, label: 'Cancelled in last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
];

function LostClientsCard({ slug }: { slug: string }) {
  const [window, setWindow] = useState<LostWindow>(60);
  const [confirming, setConfirming] = useState(false);

  const lostQ = useQuery({
    queryKey: ['admin-lists', slug, 'lost-clients', window],
    queryFn: () => fetchLostClientsPreview(slug, window),
  });

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-2 text-primary">
            <UserX className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Lost clients</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Clients whose most recent booking was cancelled and who
              haven&rsquo;t rebooked. Worth checking in on.
            </p>
          </div>
        </div>
        <Button
          leadingIcon={<Download className="h-4 w-4" />}
          onClick={() => setConfirming(true)}
          disabled={!lostQ.data || lostQ.data.totalCount === 0}
        >
          Export CSV
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {LOST_OPTIONS.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => setWindow(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              window === opt.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background text-muted-foreground hover:border-primary/40'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {lostQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : lostQ.error ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&rsquo;t load list</AlertTitle>
            <AlertDescription>{(lostQ.error as Error).message}</AlertDescription>
          </Alert>
        ) : (
          <LostClientsPreview
            totalCount={lostQ.data!.totalCount}
            rows={lostQ.data!.preview}
          />
        )}
      </div>

      <ExportConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        rowCount={lostQ.data?.totalCount ?? 0}
        downloadUrl={lostClientsCsvUrl(slug, window)}
        listLabel="Lost clients"
      />
    </Card>
  );
}

function LostClientsPreview({
  totalCount,
  rows,
}: {
  totalCount: number;
  rows: LostClientRow[];
}) {
  if (totalCount === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        No lost clients in this window. Nice.
      </p>
    );
  }
  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {totalCount} {totalCount === 1 ? 'client' : 'clients'} ·
        showing first {Math.min(rows.length, totalCount)}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Client</th>
              <th className="px-4 py-2 text-left">Last service</th>
              <th className="px-4 py-2 text-left">Cancelled</th>
              <th className="px-4 py-2 text-left">Consent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.clientId} className="border-t border-border">
                <td className="px-4 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.email ?? r.phone ?? '—'}
                  </div>
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {r.lastService}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {r.daysSinceCancellation}d ago
                </td>
                <td className="px-4 py-2">
                  {r.marketingConsent ? (
                    <Badge variant="success">Yes</Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------- Export confirm dialog ----------------------- */

function ExportConfirmDialog({
  open,
  onClose,
  rowCount,
  downloadUrl,
  listLabel,
}: {
  open: boolean;
  onClose: () => void;
  rowCount: number;
  downloadUrl: string;
  listLabel: string;
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  function onDownload() {
    // Navigate to the URL; the server responds with
    // `Content-Disposition: attachment` so the browser saves it without
    // leaving the current page. Audit-log row is written server-side.
    window.location.href = downloadUrl;
    setAcknowledged(false);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setAcknowledged(false);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export {listLabel} CSV</DialogTitle>
          <DialogDescription>
            {rowCount} {rowCount === 1 ? 'client' : 'clients'} will be
            included. The CSV has a <code>Marketing consent</code>{' '}
            column — please respect it.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Spam Act 2003</AlertTitle>
          <AlertDescription>
            Australian law requires explicit consent before sending
            marketing messages. Contacting clients with{' '}
            <code>Marketing consent = no</code> for marketing purposes
            could expose your business to penalties. Use those rows for
            non-marketing purposes only (e.g. service-related follow-ups
            tied to an existing booking).
          </AlertDescription>
        </Alert>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
          />
          <span>
            I understand and will only contact clients with{' '}
            <code>Marketing consent = yes</code> for marketing purposes.
          </span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onDownload}
            disabled={!acknowledged}
            leadingIcon={<Download className="h-4 w-4" />}
          >
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
